import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Favorite from '@mui/icons-material/Favorite';
import FavoriteBorder from '@mui/icons-material/FavoriteBorder';
import PublishIcon from '@mui/icons-material/Publish';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Divider from '@mui/material/Divider';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Skeleton from '@mui/material/Skeleton';
import { FeatureFlag } from '@cbo/shared-library';
import dayjs from 'dayjs';
import {
  cardSx,
  smallBoxSx,
  titleBoxSx,
  titleSx,
  descriptionSx,
  cardAction,
  iconContainerSx,
  moreOptionsIconButtonSx,
  heartBorder,
  menuItemSx,
  publishedIconSx,
  publishedIconBoxSx,
  dividerSx,
} from './styles';
import { useFeatureFlag } from '../../../utils/hooks/useFeatureFlag';
import ReportIcons from '../../../admin/CentralReports/components/ReportIcons/ReportIcons';
import { useUsers } from '../../../contexts/userContext';
import { checkHasAllCustomReportsViewAccess } from '../../../admin/CentralReports/utils/PublishReportUtils';
import handleEditReportNav from '../../../admin/CentralReports/utils/EditReportUtils';

// Reports that support schedule functionality
const REPORTS_WITH_SCHEDULE_SUPPORT = [21, 15, 19, 13, 3, 6, 5]; // Transaction Details, Product Mix, Sales Summary, Payment, Employee Payroll, Employee Tip, Generic Payroll Export

export interface CentralDashboardCardProps {
  description?: string;
  category?: string;
  title: string;
  url?: string;
  hideFavoriteIcon?: boolean;
  hideCategoryIcons?: boolean;
  hideMoreOptionsIcon?: boolean;
  isFavorite?: boolean;
  reportId?: number;
  reportStringId?: string;
  currentFavorites?: number[];
  isPublished?: boolean;
  lastPublished?: Date | null;
  creator?: string;
  isLoading?: boolean;
  onMoreOptionsClick?: (reportId: number) => void;
  onDeleteReportClick?: (reportId: number, reportName: string) => void;
  onScheduleReportClick?: (reportId: number | string) => void;
  onPublishReportClick?: (reportId: number, reportName: string) => void;
  onManagePublishReportClick?: (reportId: number, reportName: string) => void;
  onUnPublishReportClick?: (reportId: number, reportName: string) => void;
  onFavoritesClick?: (updatedFavorites: number[]) => void;
  moreOptionsMenuItems?: {
    id: string;
    label: string;
    onClick: () => void;
    testId?: string;
  }[];
}

export default function CentralDashboardCard({
  description,
  category,
  title,
  url,
  isFavorite,
  hideFavoriteIcon,
  hideCategoryIcons,
  hideMoreOptionsIcon,
  reportId,
  reportStringId,
  currentFavorites,
  isPublished,
  lastPublished,
  creator: reportCreator,
  isLoading = false,
  onScheduleReportClick,
  onPublishReportClick,
  onManagePublishReportClick,
  onUnPublishReportClick,
  onDeleteReportClick,
  onFavoritesClick,
  moreOptionsMenuItems,
}: Readonly<CentralDashboardCardProps>) {
  const { t } = useTranslation();
  const history = useHistory();
  const user = useUsers();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isScheduleReportEnabled = useFeatureFlag(FeatureFlag.scheduledReport);
  const isPublishReportEnabled = useFeatureFlag(FeatureFlag.publishCustomReports);
  const hasCustomMoreOptionsMenuItems = Boolean(moreOptionsMenuItems?.length);

  // Only show more icon for specific reports that support scheduling
  const isCustomReport = category === 'Custom';
  const shouldShowMoreIcon =
    (!hideMoreOptionsIcon && hasCustomMoreOptionsMenuItems) ||
    (!hideMoreOptionsIcon && reportId && REPORTS_WITH_SCHEDULE_SUPPORT.includes(reportId) && isScheduleReportEnabled) ||
    isCustomReport;

  const handlePublishIconClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleFavoriteClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    let updatedFavorites;
    if (!isFavorite) {
      updatedFavorites = [...(currentFavorites ?? []), reportId];
    } else {
      updatedFavorites = (currentFavorites ?? []).filter((id) => id !== reportId);
    }
    if (onFavoritesClick) {
      onFavoritesClick(updatedFavorites as number[]);
    }
  };

  const handleMoreOptionsClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setAnchorEl(e.currentTarget as HTMLElement);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const cardMenuHandler =
    <T extends unknown[]>(callback?: (...args: T) => void, ...args: T) =>
    () => {
      handleMenuClose();
      callback?.(...args);
    };

  const handleScheduleReport = cardMenuHandler(onScheduleReportClick, reportId ?? -1);
  const handleScheduleforPredefinedReport = cardMenuHandler(onScheduleReportClick, reportStringId ?? '');
  const handlePublishReport = cardMenuHandler(onPublishReportClick, reportId ?? -1, title);
  const handleUnPublishReport = cardMenuHandler(onUnPublishReportClick, reportId ?? -1, title);
  const handleManagePublishReport = cardMenuHandler(onManagePublishReportClick, reportId ?? -1, title);
  const handleDeleteReport = cardMenuHandler(onDeleteReportClick, reportId ?? -1, title);
  const handleCardClick = (e: React.MouseEvent) => {
    // If menu is open and user clicks elsewhere on card, close it first
    if (anchorEl) {
      setAnchorEl(null);
    }
    // Ignore if an interactive child handled the event
    if ((e.target as HTMLElement).closest('button')) {
      return;
    }
    if (url) {
      history.push(url);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleCardClick(e as unknown as React.MouseEvent);
    }
  };

  const hasAllCustomReportsViewAccess = checkHasAllCustomReportsViewAccess(user);

  const isReportOwner = Boolean(user.profile?.userId && reportCreator && user.profile?.userId === reportCreator);

  const shouldShowPublishOptions: boolean =
    isPublishReportEnabled && isCustomReport && (hasAllCustomReportsViewAccess || isReportOwner);
  const showDefaultCustomReportMenu = !hasCustomMoreOptionsMenuItems && isCustomReport;
  const publishToolTipTitle = lastPublished
    ? t('admin.centralReportsPage.lastPublished', {
        lastPublished: dayjs(lastPublished).format('MM/DD/YYYY'),
      })
    : '';

  const actionIcons = (
    <Box sx={iconContainerSx}>
      {isPublished && (
        <Tooltip title={publishToolTipTitle}>
          <Box onClick={handlePublishIconClick} sx={publishedIconBoxSx}>
            <PublishIcon sx={publishedIconSx} />
          </Box>
        </Tooltip>
      )}
      {!hideFavoriteIcon && (
        <IconButton
          aria-label={
            isFavorite
              ? t('admin.centralReportsPage.removeFromFavorites')
              : t('admin.centralReportsPage.addToFavorites')
          }
          size='small'
          onClick={handleFavoriteClick}
        >
          {isFavorite ? <Favorite color='error' /> : <FavoriteBorder sx={heartBorder} />}
        </IconButton>
      )}
      {shouldShowMoreIcon && (
        <IconButton
          aria-label='More options'
          data-testid={`report-card-more-options-${reportId ?? title}`}
          size='small'
          sx={moreOptionsIconButtonSx}
          onClick={handleMoreOptionsClick}
        >
          <MoreVertIcon />
        </IconButton>
      )}
    </Box>
  );

  return (
    <Card data-testid='central-reports-card' sx={cardSx}>
      <Box onClick={handleCardClick} onKeyDown={handleKeyDown} tabIndex={0} sx={cardAction}>
        <Box sx={smallBoxSx}>
          {hideCategoryIcons ? (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minHeight: '24px' }}>
                {isLoading ? (
                  <Skeleton variant='text' width='70%' height={28} />
                ) : (
                  <Typography sx={{ ...titleSx, pb: 1 }} color='text.primary'>
                    {title}
                  </Typography>
                )}
                {!isLoading && actionIcons}
              </Box>
              {isLoading ? (
                <Skeleton variant='text' width='80%' height={20} />
              ) : (
                <Typography sx={descriptionSx} color='text.secondary'>
                  {description}
                </Typography>
              )}
            </>
          ) : (
            <>
              <Box sx={titleBoxSx}>
                <ReportIcons
                  sectionHeader={t('admin.centralReportsPage.sectionHeaders.categoryReports', {
                    category,
                  })}
                  size='small'
                />
                {!isLoading && actionIcons}
              </Box>
              <Box sx={{ padding: '16px 16px 16px 0px' }}>
                {isLoading ? (
                  <Skeleton variant='text' width='70%' height={28} />
                ) : (
                  <Typography sx={titleSx} color='text.primary'>
                    {title}
                  </Typography>
                )}
                {isLoading ? (
                  <Skeleton variant='text' width='80%' height={20} />
                ) : (
                  <Typography sx={descriptionSx} color='text.secondary'>
                    {description}
                  </Typography>
                )}
              </Box>
            </>
          )}
        </Box>
      </Box>
      {shouldShowMoreIcon && (
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          data-testid={`report-card-more-menu-${reportId ?? title}`}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {hasCustomMoreOptionsMenuItems
            ? moreOptionsMenuItems?.map((menuItem) => (
                <MenuItem
                  key={menuItem.id}
                  data-testid={menuItem.testId}
                  onClick={cardMenuHandler(menuItem.onClick)}
                  sx={menuItemSx}
                >
                  {menuItem.label}
                </MenuItem>
              ))
            : null}

          {showDefaultCustomReportMenu && (
            <>
              <MenuItem onClick={() => (reportId ? handleEditReportNav(history, reportId) : null)} sx={menuItemSx}>
                {t('admin.customReports.editReport')}
              </MenuItem>
              <MenuItem onClick={handleDeleteReport} sx={menuItemSx}>
                {t('admin.customReports.deleteReport')}
              </MenuItem>
              {(isScheduleReportEnabled || shouldShowPublishOptions) && <Divider sx={dividerSx} />}
            </>
          )}

          {!hasCustomMoreOptionsMenuItems && isScheduleReportEnabled && (
            <MenuItem
              onClick={() => (reportStringId ? handleScheduleforPredefinedReport() : handleScheduleReport())}
              sx={menuItemSx}
            >
              {t('admin.customReports.scheduleReportSingle')}
            </MenuItem>
          )}

          {!hasCustomMoreOptionsMenuItems &&
            shouldShowPublishOptions &&
            (!isPublished ? (
              <MenuItem onClick={handlePublishReport} sx={menuItemSx}>
                {t('admin.centralReportsPage.publishReport.publishReportAction')}
              </MenuItem>
            ) : (
              <>
                <MenuItem onClick={handleManagePublishReport} sx={menuItemSx}>
                  {t('admin.centralReportsPage.publishReport.managePublishedUsersAction')}
                </MenuItem>
                <MenuItem onClick={handleUnPublishReport} sx={menuItemSx}>
                  {t('admin.centralReportsPage.publishReport.unPublishReportAction')}
                </MenuItem>
              </>
            ))}
        </Menu>
      )}
    </Card>
  );
}

CentralDashboardCard.defaultProps = {
  description: undefined,
  isPublished: false,
  lastPublished: null,
};
